import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Field,
  reduxForm,
  formValueSelector,
  initialize,
  change,
} from 'redux-form';
import { injectIntl } from 'react-intl';
import Form from 'react-bootstrap/Form';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

import Link from '../../Link';
import Loader from '../../Common/Loader';
import ImageUploadComponent from '../../../components/Common/ImageUploadComponent';
import InputFieldComponent from '../../Common/InputFieldComponent/InputFieldComponent';
import SelectFieldComponent from '../../Common/SelectFieldComponent/SelectFieldComponent';
import { demoRestriction } from '../../../actions/demoRestriction';
import { updateTempImages } from '../../../actions/siteadmin/TempImages/updateTempImages';

import messages from '../../../locale/messages';
import validate from './validate';
import submit from './submit';
import { api, categoryUploadDir } from '../../../config';
import CommonImageDisplay from '../CommonImageDisplay/CommonImageDisplay';

import bt from '../../../components/commonStyle.css';
import s from './AddCategoryForm.css';
import st from './CategoryStudioForm.css';

// Lightweight client-side slug (server still enforces uniqueness on save).
function clientSlugify(value) {
  return (value || '')
    .toString()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '');
}

const TABS = ['general', 'pricing', 'media', 'seo', 'advanced'];

export class AddCategoryForm extends Component {
  static defaultProps = {
    currency: 'USD',
    loading: false,
  };

  constructor(props) {
    super(props);
    this.state = { activeTab: 'general' };
    this.successLogo = this.successLogo.bind(this);
    this.successBanner = this.successBanner.bind(this);
    this.successIcon = this.successIcon.bind(this);
    this.handleNameChange = this.handleNameChange.bind(this);
  }

  async guardDemo() {
    const { demoRestriction, id } = this.props;
    if (id) {
      const isRestrict = await demoRestriction({ restrictType: 'category', restrictValue: id });
      return isRestrict;
    }
    return false;
  }

  async successLogo(file, fromServer) {
    if (await this.guardDemo()) return;
    const { change, updateTempImages } = this.props;
    await change('logoImage', fromServer && fromServer.fileName);
    await updateTempImages('Category', 'logoImage', fromServer && fromServer.fileName);
  }

  async successBanner(file, fromServer) {
    if (await this.guardDemo()) return;
    const { change, updateTempImages } = this.props;
    await change('bannerImage', fromServer && fromServer.fileName);
    await updateTempImages('Category', 'bannerImage', fromServer && fromServer.fileName);
  }

  async successIcon(file, fromServer) {
    if (await this.guardDemo()) return;
    const { change, updateTempImages } = this.props;
    await change('icon', fromServer && fromServer.fileName);
    await updateTempImages('Category', 'icon', fromServer && fromServer.fileName);
  }

  // Auto-fill the SEO slug from the name while the slug hasn't been customised.
  handleNameChange(e, newValue) {
    const { change, slug, name } = this.props;
    if (!slug || slug === clientSlugify(name)) {
      change('slug', clientSlugify(newValue));
    }
  }

  imgSrc(fileName) {
    return fileName ? api?.apiEndpoint + categoryUploadDir + 'medium_' + fileName : null;
  }

  renderPreview() {
    const { name, description, icon, logoImage, pricingType, isPopular, slug, metaTitle, metaDescription } = this.props;
    const { formatMessage } = this.props.intl;
    const displayName = name || formatMessage(messages.categoryName);
    const thumb = this.imgSrc(icon || logoImage);
    return (
      <div className={st.preview}>
        <div className={st.previewTitle}>Live preview</div>

        <div className={st.cardMock}>
          <div className={st.cardBanner} style={this.imgSrc(this.props.bannerImage) ? { backgroundImage: `url(${this.imgSrc(this.props.bannerImage)})` } : null} />
          <div className={st.cardBody}>
            {thumb
              ? <img className={st.cardIcon} src={thumb} alt="" />
              : <span className={st.cardIconPlaceholder}>{displayName.charAt(0).toUpperCase()}</span>}
            <div>
              <div className={st.cardName}>{displayName}</div>
              <div className={st.cardSub}>{pricingType || 'fixed'} pricing</div>
            </div>
            {(isPopular === true || isPopular === 'true') && <span className={st.cardPopular}>Popular</span>}
          </div>
        </div>

        <div className={st.previewTitle}>SEO snippet</div>
        <div className={st.seoSnippet}>
          <div className={st.seoUrl}>www.besthandapp.com/services/{slug || clientSlugify(name) || 'slug'}</div>
          <div className={st.seoTitle}>{metaTitle || displayName}</div>
          <div className={st.seoDesc}>{metaDescription || description || 'Add a meta description to control how this category appears in search results.'}</div>
        </div>
      </div>
    );
  }

  render() {
    const { handleSubmit, currency, logoImage, bannerImage, icon, loading, submitting, id, userIdentityLoading, userExperienceLoading, metaTitle, metaDescription } = this.props;
    const { formatMessage } = this.props.intl;
    const { activeTab } = this.state;

    const yesNoOptions = [
      { value: false, label: formatMessage(messages.no) },
      { value: true, label: formatMessage(messages.yes) },
    ];
    const currencyOptions = currency && currency?.availableCurrencies?.filter(item => item?.isEnable).map(item => ({ value: item?.symbol, label: item?.symbol }));
    const pricingTypeOptions = [
      { value: 'fixed', label: formatMessage(messages.fixedFareType) },
      { value: 'hourly', label: formatMessage(messages.hourlyFareType) },
    ];
    const statusOption = [
      { value: 'active', label: formatMessage(messages.active) },
      { value: 'inactive', label: formatMessage(messages.inactive) },
    ];

    const tabLabels = {
      general: 'General',
      pricing: 'Pricing',
      media: 'Media',
      seo: 'SEO',
      advanced: 'Advanced',
    };

    return (
      <Form className={s.fullWidth} onSubmit={handleSubmit(submit)}>
        <h1 className='siteAdminPrimaryHeading'>
          {id ? formatMessage(messages.editCategory) : formatMessage(messages.addCategory)}
        </h1>

        <div className={st.layout}>
          <div>
            {/* Tab bar */}
            <div className={st.tabs}>
              {TABS.map(t => (
                <button
                  key={t}
                  type="button"
                  className={cx(st.tab, activeTab === t && st.tabActive)}
                  onClick={() => this.setState({ activeTab: t })}
                >
                  {tabLabels[t]}
                </button>
              ))}
            </div>

            {/* GENERAL */}
            <div className={cx(st.panel, activeTab === 'general' && st.panelActive)}>
              <div className={st.grid2}>
                <Field name="name" type="text" component={InputFieldComponent} onChange={this.handleNameChange}
                  label={formatMessage(messages.categoryName)} placeholder={formatMessage(messages.categoryName)} inputClass={bt.formControlInput} />
                <Field name="displayOrder" type="number" component={InputFieldComponent}
                  label={'Display order'} placeholder={'Display order'} inputClass={bt.formControlInput} />
                <div className={st.fieldFull}>
                  <Field name="description" type="text" component={InputFieldComponent} componentClass="textarea"
                    label={formatMessage(messages.description)} placeholder={formatMessage(messages.description)} inputClass={bt.formTextArea} />
                </div>
                <Field name="status" component={SelectFieldComponent} options={statusOption}
                  label={formatMessage(messages.status)} inputClass={bt.formControlSelect} />
                <Field name="isPopular" component={SelectFieldComponent} options={yesNoOptions}
                  label={formatMessage(messages.isPopular)} inputClass={bt.formControlSelect} />
              </div>
            </div>

            {/* PRICING (fees are percentage-only) */}
            <div className={cx(st.panel, activeTab === 'pricing' && st.panelActive)}>
              <div className={st.grid2}>
                <Field name="pricingType" component={SelectFieldComponent} options={pricingTypeOptions}
                  label={formatMessage(messages.pricingType)} inputClass={bt.formControlSelect} />
                <Field name="currency" component={SelectFieldComponent} options={currencyOptions}
                  label={formatMessage(messages.currency)} inputClass={bt.formControlSelect} />
                <Field name="travellingPrice" type="text" maxLength={5} component={InputFieldComponent}
                  label={formatMessage(messages.travelCharge)} placeholder={formatMessage(messages.travelCharge)} inputClass={bt.formControlInput} />
                <div />
                <Field name="userServiceFeeValue" type="text" component={InputFieldComponent} isAddon suffixLabel={'%'}
                  label={formatMessage(messages.userServiceFee)} placeholder={formatMessage(messages.userServiceFee)} />
                <Field name="partnerServiceFeeValue" type="text" component={InputFieldComponent} isAddon suffixLabel={'%'}
                  label={formatMessage(messages.partnerServiceFee)} placeholder={formatMessage(messages.partnerServiceFee)} />
              </div>
            </div>

            {/* MEDIA */}
            <div className={cx(st.panel, activeTab === 'media' && st.panelActive)}>
              <div className={st.grid2}>
                <div>
                  <label className={bt.commonLabelText}>{formatMessage(messages.categoryLogo)}</label>
                  <CommonImageDisplay loader={userIdentityLoading} imageSrc={this.imgSrc(logoImage)} />
                  <ImageUploadComponent className='commonSiteAdminDropzoneSpace' subText={formatMessage(messages.maximumUploadSizeLabel)}
                    defaultMessage={formatMessage(messages.chooseFile)}
                    componentConfig={{ iconFiletypes: ['.jpg', '.png', '.svg'], multiple: false, showFiletypeIcon: false, postUrl: api.apiEndpoint + '/uploadCategoryLogo' }}
                    success={this.successLogo} loaderName={'userIdentityLoading'} />
                </div>
                <div>
                  <label className={bt.commonLabelText}>{formatMessage(messages.categoryBanner)}</label>
                  <CommonImageDisplay loader={userExperienceLoading} imageSrc={this.imgSrc(bannerImage)} />
                  <ImageUploadComponent className='commonSiteAdminDropzoneSpace' subText={formatMessage(messages.maximumUploadSizeLabel)}
                    defaultMessage={formatMessage(messages.chooseFile)}
                    componentConfig={{ iconFiletypes: ['.jpg', '.png', '.svg'], multiple: false, showFiletypeIcon: false, postUrl: api.apiEndpoint + '/uploadCategoryBanner' }}
                    success={this.successBanner} loaderName={'userExperienceLoading'} />
                </div>
                <div>
                  <label className={bt.commonLabelText}>Compact icon (optional)</label>
                  <CommonImageDisplay imageSrc={this.imgSrc(icon)} />
                  <ImageUploadComponent className='commonSiteAdminDropzoneSpace' subText={formatMessage(messages.maximumUploadSizeLabel)}
                    defaultMessage={formatMessage(messages.chooseFile)}
                    componentConfig={{ iconFiletypes: ['.jpg', '.png', '.svg'], multiple: false, showFiletypeIcon: false, postUrl: api.apiEndpoint + '/uploadCategoryLogo' }}
                    success={this.successIcon} />
                </div>
              </div>
            </div>

            {/* SEO */}
            <div className={cx(st.panel, activeTab === 'seo' && st.panelActive)}>
              <div className={st.grid2}>
                <div className={st.fieldFull}>
                  <Field name="slug" type="text" component={InputFieldComponent}
                    label={'URL slug'} placeholder={'category-url-slug'} inputClass={bt.formControlInput} />
                </div>
                <div className={st.fieldFull}>
                  <Field name="metaTitle" type="text" component={InputFieldComponent}
                    label={'Meta title'} placeholder={'Meta title'} inputClass={bt.formControlInput} />
                  <div className={st.counter}>{(metaTitle || '').length}/60</div>
                </div>
                <div className={st.fieldFull}>
                  <Field name="metaDescription" type="text" component={InputFieldComponent} componentClass="textarea"
                    label={'Meta description'} placeholder={'Meta description'} inputClass={bt.formTextArea} />
                  <div className={st.counter}>{(metaDescription || '').length}/160</div>
                </div>
              </div>
            </div>

            {/* ADVANCED */}
            <div className={cx(st.panel, activeTab === 'advanced' && st.panelActive)}>
              <div className={st.grid2}>
                <Field name="isJobPhotoRequired" component={SelectFieldComponent} options={yesNoOptions}
                  label={formatMessage(messages.isJobPhotoRequired)} inputClass={bt.formControlSelect} />
              </div>
            </div>
          </div>

          {this.renderPreview()}
        </div>

        <div className='commonButtonContainer'>
          <Link to={'/siteadmin/category'} className={bt.btnSecondary}>{formatMessage(messages.goBack)}</Link>
          <Loader type={'button'} label={formatMessage(messages.submitButton)} show={loading}
            buttonType={'submit'} className={cx(bt.btnPrimary, 'loaderButtonFlex')} disabled={submitting || loading} />
        </div>
      </Form>
    );
  }
}

AddCategoryForm = reduxForm({
  form: 'AddCategoryForm',
  onSubmit: submit,
  validate,
})(AddCategoryForm);

const selector = formValueSelector('AddCategoryForm');

const mapState = state => ({
  currency: state?.currency,
  id: selector(state, 'id'),
  name: selector(state, 'name'),
  description: selector(state, 'description'),
  logoImage: selector(state, 'logoImage'),
  bannerImage: selector(state, 'bannerImage'),
  icon: selector(state, 'icon'),
  pricingType: selector(state, 'pricingType'),
  isPopular: selector(state, 'isPopular'),
  slug: selector(state, 'slug'),
  metaTitle: selector(state, 'metaTitle'),
  metaDescription: selector(state, 'metaDescription'),
  loading: state?.loader?.AddCategory,
  userIdentityLoading: state?.loader?.userIdentityLoading,
  userExperienceLoading: state?.loader?.userExperienceLoading,
});

const mapDispatch = {
  initialize,
  change,
  updateTempImages,
  demoRestriction,
};

export default injectIntl(
  withStyles(bt, s, st)(connect(mapState, mapDispatch)(AddCategoryForm)),
);
